<template>
  <div>
    <StatTiles
      :tiles="customerTiles"
      :loading="loading"
      class="mb-6"
    />

    <!-- Print orders. Called out on its own because print is where the revenue is, and the attach rate is
         the number that says whether this audience needs print acquisition or print retention. -->
    <v-card
      outlined
      class="mb-6"
    >
      <v-card-title class="text-subtitle-1">
        Print orders
        <v-spacer />
        <span class="text-h6 success--text">{{ customers.printRevenue | currencyShort }}</span>
      </v-card-title>
      <v-card-text>
        <p class="text-body-2">
          {{ printSummary }}
        </p>

        <v-row dense>
          <v-col
            v-for="metric in printMetrics"
            :key="metric.label"
            cols="6"
            md="3"
          >
            <div class="text-subtitle-1">
              {{ metric.value }}
            </div>
            <div class="text-caption grey--text">
              {{ metric.label }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <div class="text-caption grey--text mb-1">
          Subscription revenue
        </div>
        <div class="text-subtitle-1">
          {{ customers.subscriptionRevenue | currencyShort }}
          <span class="text-caption grey--text">
            · {{ revenueMix }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Email engagement. Rolled up from the Customer.io reporting events across every send AIMED at this
         audience — broadcasts to everyone are excluded server-side, or every audience would show the same
         numbers. Rates are per-send uniques summed, not distinct-people-ever. -->
    <v-card
      outlined
      class="mb-6"
    >
      <v-card-title class="text-subtitle-1">
        Email engagement
        <v-spacer />
        <span
          v-if="email.openRate != null"
          class="text-h6"
        >{{ email.openRate | percentage }} open</span>
      </v-card-title>
      <v-card-text>
        <div
          v-if="email.error"
          class="text-body-2 error--text"
        >
          {{ email.error }}
        </div>

        <template v-else>
          <p class="text-body-2">
            {{ emailSummary }}
          </p>

          <v-row
            v-if="email.sends"
            dense
          >
            <v-col
              v-for="metric in emailMetrics"
              :key="metric.label"
              cols="6"
              md="3"
            >
              <div class="text-subtitle-1">
                {{ metric.value }}
              </div>
              <div class="text-caption grey--text">
                {{ metric.label }}
              </div>
            </v-col>
          </v-row>

          <div
            v-for="note in emailNotes"
            :key="note"
            class="text-caption grey--text mt-2"
          >
            {{ note }}
          </div>
        </template>
      </v-card-text>
    </v-card>

    <!-- AI cost. Split three ways on purpose: an industry-wide campaign reaches this audience without being
         only its campaign, so blending that into one figure would make every topic under an industry look
         like it spent the same money. Plan + own campaigns is this audience's alone; the inherited share is
         stated separately and labelled as shared. -->
    <v-card
      v-if="aiSpend.calls"
      outlined
      class="mb-6"
    >
      <v-card-title class="text-subtitle-1">
        AI cost
        <v-spacer />
        <span class="text-h6">{{ aiSpend.total | currencyAi }}</span>
      </v-card-title>
      <v-card-text>
        <p class="text-body-2">
          {{ aiSummary }}
        </p>

        <v-row dense>
          <v-col
            v-for="metric in aiMetrics"
            :key="metric.label"
            cols="6"
            md="3"
          >
            <div class="text-subtitle-1">
              {{ metric.value }}
            </div>
            <div class="text-caption grey--text">
              {{ metric.label }}
            </div>
          </v-col>
        </v-row>

        <div
          v-if="aiSpend.inherited.cost"
          class="text-caption grey--text mt-2"
        >
          Industry-wide spend is shared with the other audiences the same campaigns reached, so it is counted
          here but is not this audience's alone. Plan and own-campaign spend belong to this audience only.
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Existing customers: who we already have, and the authoritative mailable size. -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          outlined
          class="fill-height"
        >
          <v-card-title class="text-subtitle-1">
            Existing customers
          </v-card-title>
          <v-card-text>
            <p class="text-body-2">
              {{ existingSummary }}
            </p>

            <div class="text-caption grey--text mb-1">
              Customer.io work areas targeted
            </div>
            <div v-if="workAreas.length">
              <v-chip
                v-for="area in workAreas"
                :key="area"
                x-small
                class="mr-1 mb-1"
              >
                {{ area }}
              </v-chip>
            </div>
            <div
              v-else
              class="text-caption error--text mb-2"
            >
              No work areas — an email to this audience would go to everyone.
            </div>

            <v-divider class="my-3" />

            <div class="d-flex align-center">
              <div>
                <div class="text-caption grey--text">
                  Mailable size (Customer.io)
                </div>
                <div class="text-subtitle-1">
                  {{ cioLabel }}
                </div>
              </div>
              <v-spacer />
              <v-btn
                small
                outlined
                color="mcsprimary"
                :loading="loadingCount"
                @click="loadCount"
              >
                {{ cioCount === null ? 'Check' : 'Recheck' }}
              </v-btn>
            </div>
            <div
              v-if="cioError"
              class="text-caption error--text mt-1"
            >
              {{ cioError }}
            </div>
            <div
              v-else-if="cioPartial"
              class="text-caption grey--text mt-1"
            >
              Customer.io caps how far we page, so this is a floor — the real audience is larger.
            </div>

            <v-expansion-panels
              v-if="customers.recipientsFilter"
              flat
              class="mt-3"
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="text-caption pa-0">
                  Recipients filter
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <pre class="cio-filter">{{ customers.recipientsFilter }}</pre>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- New customers: whether the acquisition surfaces this audience needs actually exist. -->
      <v-col
        cols="12"
        md="6"
      >
        <v-card
          outlined
          class="fill-height"
        >
          <v-card-title class="text-subtitle-1">
            New customers
          </v-card-title>
          <v-card-text>
            <p class="text-body-2">
              {{ newSummary }}
            </p>

            <v-simple-table dense>
              <tbody>
                <tr
                  v-for="row in acquisitionRows"
                  :key="row.label"
                >
                  <td class="text-caption">
                    {{ row.label }}
                  </td>
                  <!-- Board names are keyword-rich and long, so give them room rather than truncating. -->
                  <td class="text-right">
                    <v-icon
                      v-if="row.boolean"
                      small
                      :color="row.ok ? 'success' : 'error'"
                    >
                      {{ row.ok ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
                    </v-icon>
                    <span
                      v-else
                      :class="row.ok ? '' : 'error--text'"
                    >{{ row.value }}</span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <v-alert
              v-if="seasonalNote"
              dense
              text
              type="info"
              class="mt-3 mb-0 text-caption"
            >
              {{ seasonalNote }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import StatTiles from '@/components/common/StatTiles';

export default {
  name: 'AudienceOverview',
  components: { StatTiles },
  props: {
    overview: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loadingCount: false,
      cioCount: null,
      cioPartial: false,
      cioError: ''
    };
  },
  computed: {
    customers() {
      return this.overview.customers || {};
    },
    customerTiles() {
      const s = this.customers;
      return [
        { label: 'Customers', value: this.$options.filters.separator(s.total || 0) },
        { label: 'Active (90d)', value: this.$options.filters.separator(s.active || 0), color: 'success' },
        { label: 'Lapsed', value: this.$options.filters.separator(s.lapsed || 0), color: 'warning' },
        { label: 'Subscribers', value: this.$options.filters.separator(s.subscribers || 0) },
        { label: 'Print buyers', value: this.$options.filters.separator(s.printCustomers || 0) },
        // Whole dollars — cents are noise at audience scale and would wrap the tile.
        { label: 'Print revenue', value: this.$options.filters.currencyShort(s.printRevenue || 0), color: 'success' }
      ];
    },
    coverage() {
      return (this.overview.gaps || {}).coverage || {};
    },
    workAreas() {
      return this.customers.workAreas || [];
    },
    email() {
      return this.overview.email || {};
    },
    // Defaulted all the way down so the card's guards work on an overview that predates the field.
    aiSpend() {
      const s = this.overview.aiSpend || {};
      const bucket = (b) => ({ cost: 0, calls: 0, campaigns: 0, ...(b || {}) });
      return {
        plan: bucket(s.plan),
        direct: bucket(s.direct),
        inherited: bucket(s.inherited),
        total: s.total || 0,
        own: s.own || 0,
        calls: s.calls || 0
      };
    },
    aiMetrics() {
      const s = this.aiSpend;
      const f = this.$options.filters;
      return [
        { label: 'This audience only', value: f.currencyAi(s.own) },
        { label: 'Plan drafting', value: f.currencyAi(s.plan.cost) },
        {
          label: `Own campaigns (${s.direct.campaigns})`,
          value: f.currencyAi(s.direct.cost)
        },
        {
          label: `Industry-wide (${s.inherited.campaigns}, shared)`,
          value: f.currencyAi(s.inherited.cost)
        }
      ];
    },
    aiSummary() {
      const s = this.aiSpend;
      const f = this.$options.filters;
      const own = `${f.currencyAi(s.own)} has been spent on this audience alone`;

      if (!s.inherited.cost) {
        return `${own}, across ${s.calls} billed AI calls.`;
      }

      return (
        `${own}. A further ${f.currencyAi(s.inherited.cost)} went on ` +
        `${s.inherited.campaigns} wider campaign${s.inherited.campaigns === 1 ? '' : 's'} that also reached it.`
      );
    },
    emailMetrics() {
      const e = this.email;
      const f = this.$options.filters;
      return [
        { label: 'Targeted sends', value: f.separator(e.sends || 0) },
        { label: 'Delivered', value: f.separator(e.delivered || e.sent || 0) },
        { label: 'Click rate', value: e.clickRate == null ? '—' : f.percentage(e.clickRate) },
        // Clicks per opener: the copy signal, and the one rate deliverability can't flatter.
        { label: 'Click to open', value: e.clickToOpenRate == null ? '—' : f.percentage(e.clickToOpenRate) }
      ];
    },
    // Plain-language read: is this audience engaged, ignored, or being over-mailed?
    emailSummary() {
      const e = this.email;
      const f = this.$options.filters;

      if (!e.sends) {
        return e.everyoneSendsExcluded
          ? 'No email has been aimed at this audience yet — it has only been reached by broadcasts that went to everyone.'
          : 'No email has been sent to this audience yet, so there is nothing to measure.';
      }
      if (e.openRate == null) {
        return `${f.separator(e.sends)} send${e.sends === 1 ? '' : 's'} went out, but no reporting events came back for them yet.`;
      }

      const parts = [
        `${f.separator(e.sends)} targeted send${e.sends === 1 ? '' : 's'} reached ` +
          `${f.separator(e.delivered || e.sent || 0)} inboxes, ${f.percentage(e.openRate)} of which opened.`
      ];

      if (e.openRate < 0.15) parts.push('That is low — this audience is tuning the mail out, so the subject lines are the first thing to change.');
      else if (e.clickToOpenRate != null && e.clickToOpenRate < 0.05) parts.push('They open but rarely click, so the subject lines are working and the offer inside is not.');
      else parts.push('This audience is engaged — worth mailing more often.');

      if ((e.unsubscribeRate || 0) > 0.005) parts.push(`Unsubscribes are running at ${f.percentage(e.unsubscribeRate)}, which is high enough to back off the frequency.`);

      return parts.join(' ');
    },
    // The caveats that decide whether the rates above can be trusted at all.
    emailNotes() {
      const e = this.email;
      const notes = [];
      if (!e.sends && !e.everyoneSendsExcluded) return notes;

      if (e.windowStart) {
        notes.push(`Sends since ${new Date(e.windowStart).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}. Reporting events are kept for 400 days.`);
      }
      if (e.sendsMissingData) {
        notes.push(`${e.sendsMissingData} of these sends have no reporting data — sent before the Customer.io webhook went live, or the events have aged out.`);
      }
      if (e.everyoneSendsExcluded) {
        notes.push(`${e.everyoneSendsExcluded} broadcast${e.everyoneSendsExcluded === 1 ? '' : 's'} to everyone reached this audience but ${e.everyoneSendsExcluded === 1 ? 'is' : 'are'} excluded — they say nothing about how this audience specifically responds.`);
      }
      return notes;
    },
    cioLabel() {
      if (this.cioCount === null) return 'Not checked';
      return this.cioCount.toLocaleString() + (this.cioPartial ? '+' : '');
    },
    printMetrics() {
      const s = this.customers;
      const f = this.$options.filters;
      return [
        { label: 'Print orders', value: f.separator(s.printOrders || 0) },
        { label: 'Print buyers', value: f.separator(s.printCustomers || 0) },
        { label: 'Buy print', value: f.percentage(s.printAttachRate || 0) },
        { label: 'Avg order value', value: f.currency(s.avgPrintOrderValue || 0) }
      ];
    },
    // The print read is the point of this whole screen: is this audience an acquisition or a retention play?
    printSummary() {
      const s = this.customers;
      if (!s.total) return 'Nobody has told us they work in this area yet.';
      if (!s.printOrders) {
        return 'This audience has never ordered print. Everything here is upside — but check the content gaps first, ' +
          'because there needs to be something worth printing.';
      }

      const rate = this.$options.filters.percentage(s.printAttachRate || 0);
      if ((s.printAttachRate || 0) < 0.1) {
        return `Only ${rate} of this audience has ever ordered print, so the opportunity is getting the other ` +
          `${this.$options.filters.separator(s.total - s.printCustomers)} to their first order — lead with a print offer, not a design one.`;
      }
      return `${rate} of this audience orders print, averaging ` +
        `${this.$options.filters.currency(s.printRevenue / Math.max(1, s.printCustomers))} each. ` +
        'This segment already prints — push repeat orders and larger runs.';
    },
    revenueMix() {
      const s = this.customers;
      const total = (s.printRevenue || 0) + (s.subscriptionRevenue || 0);
      if (!total) return 'no revenue yet';

      const printShare = (s.printRevenue || 0) / total;
      return `${this.$options.filters.percentage(printShare)} of revenue from this audience is print`;
    },
    // Plain-language read of the local numbers — no AI, just the largest actionable segment.
    existingSummary() {
      const s = this.customers;
      if (!s.total) return 'Nobody has told us they work in this area yet.';

      const parts = [`${s.total.toLocaleString()} people work in this area.`];
      if (s.lapsed > s.active) parts.push(`Most (${s.lapsed.toLocaleString()}) haven't signed in for 90 days — this is a re-engagement audience first.`);
      else parts.push(`${s.active.toLocaleString()} are active.`);
      if (s.churned) parts.push(`${s.churned.toLocaleString()} subscribed before and don't now — a win-back segment.`);
      else if (s.free) parts.push(`${s.free.toLocaleString()} are on free plans.`);
      return parts.join(' ');
    },
    newSummary() {
      const c = this.coverage;
      if (!c.hasForPage) return "There's no landing page for this audience, so paid and organic traffic has nowhere good to land.";
      if (!c.forPagePublished) return 'A landing page exists but is not live yet — finish it before driving traffic here.';
      if (!c.publishedBlogPostCount) return 'The landing page is live, but there is no blog coverage bringing search traffic to it.';
      return 'The acquisition surfaces are in place. Drive traffic to them.';
    },
    acquisitionRows() {
      const c = this.coverage;
      return [
        { label: 'Landing page live', boolean: true, ok: !!c.forPagePublished },
        { label: 'Published templates', value: c.templateCount || 0, ok: (c.templateCount || 0) >= 20 },
        // Idea pages are one per design group, so the count only means something against how many groups this
        // audience has — "3" reads fine until you learn there are 40.
        {
          label: 'Idea pages',
          value: c.designGroupCount
            ? `${c.ideaPageCount || 0} of ${c.designGroupCount}`
            : c.ideaPageCount || 0,
          ok: (c.ideaPageCount || 0) > 0
        },
        { label: 'Blog posts', value: c.publishedBlogPostCount || 0, ok: (c.publishedBlogPostCount || 0) > 0 },
        {
          label: 'Pinterest board',
          boolean: !c.pinterestBoardName,
          ok: !!c.hasPinterestBoard,
          value: c.pinterestBoardName
        }
      ];
    },
    seasonalNote() {
      const c = this.coverage;
      if (!c.marketingStartDate) return '';

      const start = new Date(c.marketingStartDate).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      });
      const end = c.marketingEndDate
        ? new Date(c.marketingEndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : null;

      const window = end ? `${start} – ${end}` : start;
      return c.inMarketingRange
        ? `This audience is in season right now (${window}).`
        : `This audience is seasonal (${window}).`;
    }
  },
  methods: {
    loadCount() {
      this.loadingCount = true;
      this.cioError = '';
      this.$store
        .dispatch('audiences/getCustomerCount', {
          targetType: this.overview.targetType,
          targetId: this.overview.targetId
        })
        .then((data) => {
          this.cioCount = data.customerIoCount === null ? 0 : data.customerIoCount;
          this.cioPartial = !!data.customerIoIsPartial;
          this.cioError = data.customerIoError || '';
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loadingCount = false;
        });
    }
  }
};
</script>

<style scoped>
.cio-filter {
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}
</style>
